/*
  PDP — gallery filtering by Color AND Size.

  ---------- Why this exists ----------

  The theme filters gallery images from an alt-tag convention (`#color_navy`),
  and it does the work in two places:

    1. snippets/product-media.liquid, for the first render
    2. ProductMedia._onVariantChanged in assets/theme.js, on every variant change

  Both read only the LAST `#` group of the alt, so an image can be tied to one
  option and no more. The Liquid half has been widened (behind a template test)
  to AND every group together and to understand a virtual `fit` option. This
  file is the other half: without it the gallery would be correct on load and
  wrong the moment someone picked a size.

  ---------- Why it does not patch theme.js ----------

  theme.js is a minified bundle served on every page of the store. Editing it to
  fix one template would be a site-wide change to a build artefact, reapplied by
  hand after every theme update. Instead this runs alongside it.

  The two do not fight, because of how theme.js reads the alt. For an image
  tagged `#color_navy#fit_m` it takes the last group, `fit_m`, looks for a
  product option called "fit", finds none, and filters nothing. So it leaves
  multi-tagged images alone and this file owns the decision for them. Images
  still carrying a single `#color_x` tag are handled by both, identically.

  Ordering is handled explicitly rather than by luck — see the microtask in
  onChange.
*/

(function () {
  'use strict';

  class PdpGalleryFilter extends HTMLElement {
    connectedCallback() {
      this.gallery = document.querySelector('product-media[data-fit-buckets]');
      if (!this.gallery) return;

      this.buckets = this.parseBuckets(this.gallery.dataset.fitBuckets);

      var form = document.getElementById(this.gallery.getAttribute('form-id'));
      if (!form) return;

      this.onChange = this.onChange.bind(this);
      form.addEventListener('variant:changed', this.onChange);
      this.form = form;

      this.schedulePreload();
    }

    /**
     * Fetch the rest of the gallery in the background once the page has
     * settled, so switching color or size shows its photos straight away
     * instead of waiting 150-300ms for each set to download.
     *
     * The whole gallery at the widths a phone picks is about 1.3MB, and the
     * part not already on screen is about 0.8MB. That is cheap enough to fetch
     * up front, but only after everything the shopper can see: it waits for
     * `load`, then an idle moment, and asks at low priority.
     *
     * Skipped where the shopper has asked to save data or is on a slow
     * connection, where they keep the on-demand loading they had before.
     * Safari exposes neither signal, so there it always runs.
     *
     * Download only. The images stay hidden, so the browser caches them
     * without drawing them, and nothing is decoded until a set is shown.
     */
    schedulePreload() {
      var connection = navigator.connection;
      if (connection && (connection.saveData || /(^|-)2g$|^3g$/.test(connection.effectiveType || ''))) {
        return;
      }

      var start = function () {
        var idle = window.requestIdleCallback || function (fn) { return window.setTimeout(fn, 1500); };
        idle(this.preload.bind(this), { timeout: 4000 });
      }.bind(this);

      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }

    preload() {
      Array.prototype.forEach.call(
        this.gallery.querySelectorAll('img[loading="lazy"]'),
        function (img) {
          if (img.complete && img.naturalWidth) return;
          img.fetchPriority = 'low';
          img.loading = 'eager';
        }
      );
    }

    disconnectedCallback() {
      this.form?.removeEventListener('variant:changed', this.onChange);
      this.pending = null;
    }

    /**
     * "s:S|m:M,L|xl:XL,2XL,3XL,4XL" -> { s:['s'], m:['m','l'], xl:['xl',...] }
     *
     * Parsed from the attribute rather than hard-coded here, so the bands are
     * defined in exactly one place — FIT_BUCKETS in product-media.liquid, which
     * is also what the server render uses. Two copies of this map would drift
     * and the drift would only show on variant change.
     */
    parseBuckets(raw) {
      var out = {};
      (raw || '').split('|').forEach(function (pair) {
        var bits = pair.split(':');
        if (bits.length < 2) return;
        out[bits[0].trim().toLowerCase()] = bits[1]
          .split(',')
          .map(function (s) { return s.trim().toLowerCase(); })
          .filter(Boolean);
      });
      return out;
    }

    bucketFor(size) {
      var needle = (size || '').trim().toLowerCase();
      for (var name in this.buckets) {
        if (this.buckets[name].indexOf(needle) !== -1) return name;
      }
      return '';
    }

    onChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      // theme.js handles the same event and rewrites every is-filtered class
      // from its own, narrower calculation. A microtask puts this after that,
      // whatever order the two listeners were registered in: the event is
      // dispatched from script, so no microtasks run until every listener has
      // returned.
      //
      // It must be a microtask and not the next animation frame. A frame
      // later, the browser has already painted theme.js's unfiltered gallery,
      // which is taller, and the page visibly jumps.
      var token = (this.pending = {});
      window.queueMicrotask(function () {
        if (this.pending !== token) return;
        this.pending = null;
        this.apply(variant);
      }.bind(this));
    }

    apply(variant) {
      var options = this.optionMap(variant);
      var changed = false;

      // Decide once per MEDIA ID, then apply to every element carrying that id.
      //
      // The gallery renders each image twice: a slide in the main carousel and
      // a thumbnail in the rail beside it. Only the slides carry
      // data-media-alt, and this used to read the alt off each element — so
      // the thumbnails were skipped, while theme.js (which un-filters anything
      // it can't parse) reset them. Result: the main carousel stayed correct
      // and the thumbnail rail filled with every size and color on the first
      // variant change. Keying by id makes the two lists one decision.
      var altById = {};
      Array.prototype.forEach.call(
        this.gallery.querySelectorAll('[data-media-id][data-media-alt]'),
        function (item) { altById[item.dataset.mediaId] = item.dataset.mediaAlt; }
      );

      Array.prototype.forEach.call(
        this.gallery.querySelectorAll('[data-media-id]'),
        function (item) {
          var alt = altById[item.dataset.mediaId];
          if (alt === undefined) return;

          var hide = this.shouldHide(alt, options);
          if (item.classList.contains('is-filtered') !== hide) changed = true;
          item.classList.toggle('is-filtered', hide);
        }.bind(this)
      );

      if (!changed) return;

      // Something became visible or hidden, so the carousel's cell list is
      // stale. Without this the slide count and the arrows keep counting images
      // that are no longer on screen.
      this.reselect();
    }

    /**
     * The variant's option values, keyed by lower-cased option name, plus the
     * derived `fit` bucket. `variant.options` is positional, so the names come
     * from the product option list theme.js already loaded onto the gallery.
     */
    optionMap(variant) {
      var map = {};
      var names = this.optionNames();

      (variant.options || []).forEach(function (value, index) {
        var name = names[index];
        if (name) map[name] = String(value).trim().toLowerCase();
      });

      map.fit = this.bucketFor(map.size);
      return map;
    }

    optionNames() {
      if (this.names) return this.names;

      // Straight off the gallery element, in position order, written there by
      // Liquid. Scraping them out of the form markup would mean depending on
      // the radio groups rendering in option order, which nothing guarantees.
      this.names = (this.gallery.dataset.optionNames || '')
        .split(',')
        .map(function (n) { return n.trim().toLowerCase(); });

      return this.names;
    }

    /**
     * True when any `#group` in the alt disagrees with the current selection.
     * Groups are ANDed; a group naming an option that does not exist is
     * ignored rather than treated as a mismatch, so a typo hides nothing.
     */
    shouldHide(alt, options) {
      if (alt.indexOf('#') === -1) return false;

      return alt.split('#').some(function (part) {
        var group = part.trim();
        var cut = group.indexOf('_');
        if (cut === -1) return false;

        var name = group.slice(0, cut).trim().toLowerCase();
        var value = group.slice(cut + 1).trim().toLowerCase();

        var current = options[name];
        if (current === undefined) return false;

        return current !== value;
      });
    }

    /**
     * Keeps the carousel honest after the visible set changes: select the first
     * image that survived the filter, then ask Flickity to rebuild.
     */
    reselect() {
      var visible = this.gallery.querySelector(
        '.product__media-item:not(.is-filtered)'
      );

      if (visible) {
        Array.prototype.forEach.call(
          this.gallery.querySelectorAll('[data-media-id]'),
          function (item) {
            var on = item.dataset.mediaId === visible.dataset.mediaId;
            item.classList.toggle('is-selected', on);
            item.classList.toggle('is-initial-selected', on);
          }
        );
      }

      var carousel = this.gallery.querySelector('flickity-carousel');
      if (carousel && typeof carousel.reload === 'function') carousel.reload();
    }
  }

  if (!window.customElements.get('v2-pdp-gallery-filter')) {
    window.customElements.define('v2-pdp-gallery-filter', PdpGalleryFilter);
  }
})();
